import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import Reviews from "@/components/Reviews";
import HeartButton from "@/components/HeartButton";
import ReportButton from "@/components/ReportButton";
import { useAuth } from "@/context/AuthContext";
import { thumb } from "@/lib/img";
import { SunTimes } from "@/components/SunTimes";

// Extracted to avoid duplication between Home.jsx and Photographer.jsx.
// Mobile-safe: dialog scrolls internally, image caps at 45vh on <md.
export default function PhotoLightbox({ photo, open, onOpenChange, showPhotographerLink = true }) {
    const { user } = useAuth();
    const gallery = photo ? (photo.images?.length ? photo.images : [photo.image_url]) : [];
    const [idx, setIdx] = useState(0);

    useEffect(() => { setIdx(0); }, [photo?.photo_id]);
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, gallery.length - 1));
            if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, gallery.length]);

    // Touch swipe
    const touchRef = useState({ x: 0 })[0];
    const onTouchStart = (e) => {
        const t = e.touches?.[0]; if (!t) return;
        touchRef.x = t.clientX;
    };
    const onTouchEnd = (e) => {
        const t = e.changedTouches?.[0]; if (!t) return;
        const dx = t.clientX - touchRef.x;
        if (Math.abs(dx) < 40) return;
        if (dx < 0) setIdx((i) => Math.min(i + 1, gallery.length - 1));
        else setIdx((i) => Math.max(i - 1, 0));
    };

    if (!photo) return null;
    const hasMultiple = gallery.length > 1;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
                <VisuallyHidden.Root asChild>
                    <DialogTitle>{photo.title}</DialogTitle>
                </VisuallyHidden.Root>
                <div className="grid md:grid-cols-[1.6fr_1fr]">
                    <div className="relative bg-black flex items-center justify-center select-none"
                        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                        <img
                            src={thumb(gallery[idx], 1600)}
                            alt={photo.title}
                            data-testid="photo-lightbox-image"
                            className="max-w-full max-h-[45vh] md:max-h-[85vh] object-contain w-full"
                        />
                        {hasMultiple && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setIdx((i) => Math.max(i - 1, 0))}
                                    disabled={idx === 0}
                                    data-testid="photo-lightbox-prev"
                                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-foreground flex items-center justify-center shadow disabled:opacity-30 disabled:cursor-not-allowed"
                                    aria-label="Previous image"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIdx((i) => Math.min(i + 1, gallery.length - 1))}
                                    disabled={idx === gallery.length - 1}
                                    data-testid="photo-lightbox-next"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-foreground flex items-center justify-center shadow disabled:opacity-30 disabled:cursor-not-allowed"
                                    aria-label="Next image"
                                >
                                    <ChevronRight size={18} />
                                </button>
                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid="photo-lightbox-dots">
                                    {gallery.map((_, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            data-testid={`photo-lightbox-dot-${i}`}
                                            onClick={() => setIdx(i)}
                                            aria-label={`Show image ${i + 1}`}
                                            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                                        />
                                    ))}
                                </div>
                                <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/50 text-white text-[10px] font-bold tracking-wider" data-testid="photo-lightbox-counter">
                                    {idx + 1} / {gallery.length}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="p-6 md:p-8 flex flex-col">
                        <div className="flex items-start justify-between gap-2 pr-10">
                            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground min-w-0">
                                {photo.location_name}
                            </div>
                            <HeartButton kind="photo" id={photo.photo_id} size={22} className="w-10 h-10 -mt-1 -mr-1 shrink-0 hover:bg-secondary" testidPrefix="lightbox-heart" />
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl font-black mt-2">{photo.title}</h2>
                        {photo.description && (
                            <p className="text-sm text-muted-foreground mt-2">{photo.description}</p>
                        )}
                        <SunTimes lat={photo.lat} lng={photo.lng} />
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {(photo.tags || []).map((t) => (
                                <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-secondary font-semibold uppercase tracking-wider">{t}</span>
                            ))}
                        </div>
                        {showPhotographerLink && (
                            <Link
                                to={`/photographer/${photo.user_id}`}
                                data-testid="photo-photographer-link"
                                onClick={() => onOpenChange(false)}
                                className="mt-5 flex items-center gap-3 p-3 rounded-2xl border border-stone-200 hover:bg-secondary transition-colors"
                            >
                                {photo.photographer_picture && (
                                    <img loading="lazy" decoding="async" src={thumb(photo.photographer_picture, 200)} alt="" className="w-12 h-12 rounded-full object-cover" />
                                )}
                                <div className="flex-1">
                                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Photographer</div>
                                    <div className="font-display text-lg font-bold">{photo.photographer_name}</div>
                                </div>
                                <ExternalLink size={14} className="text-muted-foreground" />
                            </Link>
                        )}
                        {(photo.best_time_of_day || photo.best_time_of_year || photo.access || photo.tips) && (
                            <div className="mt-4 rounded-xl border border-stone-200 bg-secondary/30 p-3 space-y-1.5 text-xs" data-testid="photo-spot-details">
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Spot details</div>
                                {photo.best_time_of_day && (
                                    <div><b className="text-foreground">Best time of day:</b> <span className="text-muted-foreground capitalize">{photo.best_time_of_day.replace("_", " ")}</span></div>
                                )}
                                {photo.best_time_of_year && (
                                    <div><b className="text-foreground">Best season:</b> <span className="text-muted-foreground capitalize">{photo.best_time_of_year}</span></div>
                                )}
                                {photo.access && (
                                    <div><b className="text-foreground">Access:</b> <span className="text-muted-foreground capitalize">{photo.access.replace("_", " ")}</span></div>
                                )}
                                {photo.tips && (
                                    <div><b className="text-foreground">Tips:</b> <span className="text-muted-foreground">{photo.tips}</span></div>
                                )}
                            </div>
                        )}
                        <div className="mt-6 space-y-2">
                            {photo.mode === "prints" && photo.price != null && (
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Print from</div>
                                    <div className="font-display text-2xl font-black">£{Number(photo.price).toFixed(2)}</div>
                                </div>
                            )}
                            {/* Legacy photos without a mode still show the price if they have one. */}
                            {!photo.mode && photo.price != null && (
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Print from</div>
                                    <div className="font-display text-2xl font-black">£{Number(photo.price).toFixed(2)}</div>
                                </div>
                            )}
                            {photo.mode === "prints" && photo.print_on_demand_url && (
                                <a href={photo.print_on_demand_url} target="_blank" rel="noreferrer" data-testid="photo-pod-link">
                                    <Button variant="outline" className="w-full rounded-full mt-1">
                                        Order print via partner <ExternalLink size={12} className="ml-1.5" />
                                    </Button>
                                </a>
                            )}
                            {photo.mode === "booking" && (
                                <div data-testid="photo-booking-cta">
                                    {photo.booking_price != null && (
                                        <div>
                                            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Book a shoot from</div>
                                            <div className="font-display text-2xl font-black">£{Number(photo.booking_price).toFixed(2)}</div>
                                        </div>
                                    )}
                                    {showPhotographerLink && (
                                        <Link to={`/photographer/${photo.user_id}`} onClick={() => onOpenChange(false)}>
                                            <Button className="w-full rounded-full bg-accent hover:bg-accent/90 mt-2" data-testid="photo-book-me">
                                                Book me for a shoot
                                            </Button>
                                        </Link>
                                    )}
                                </div>
                            )}
                            {showPhotographerLink && photo.mode !== "booking" && (
                                <Link to={`/photographer/${photo.user_id}`} onClick={() => onOpenChange(false)}>
                                    <Button className="w-full rounded-full bg-primary hover:bg-primary/90 hover:text-white mt-1" data-testid="photo-visit-photographer">
                                        Visit photographer profile
                                    </Button>
                                </Link>
                            )}
                        </div>
                        <div className="mt-6 border-t border-stone-200 pt-4">
                            <Reviews targetType="photo" targetId={photo.photo_id} ownerId={photo.user_id} testid="photo-reviews" />
                            <div className="mt-4 flex justify-end">
                                <ReportButton
                                    targetType="photo"
                                    targetId={photo.photo_id}
                                    targetLabel={photo.title}
                                    hideForOwner={user?.user_id === photo.user_id}
                                    variant="compact"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
